import React, { useState, useEffect, useRef } from 'react';

// Configuration - Parsed from environment variables at build time
// Use REACT_APP_* prefix for Create React App to inject these at build time
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://6nyr4apta8.execute-api.us-east-1.amazonaws.com/lab';
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://qktkg6zhbe.execute-api.us-east-1.amazonaws.com/lab';

// Cognito Configuration - Parsed from environment variables at build time
const COGNITO_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_uX0MwlT7u',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '6n89prkmv3o48mde311v5qtvm',
};

// SSO Configuration
const SSO_CONFIG = {
  enabled: process.env.REACT_APP_SSO_ENABLED === 'true',
  samlEnabled: process.env.REACT_APP_SAML_ENABLED === 'true',
  oidcEnabled: process.env.REACT_APP_OIDC_ENABLED === 'true',
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || 'de-sos-lab-auth',
  samlProviderName: process.env.REACT_APP_SAML_PROVIDER_NAME || 'NBCUEntraID',
  oidcProviderName: process.env.REACT_APP_OIDC_PROVIDER_NAME || 'AzureADB2C',
};

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token')); // ID token for API Gateway
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken')); // Access token for WebSocket
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refreshToken')); // Refresh token for renewing access
  const [tokenExpiry, setTokenExpiry] = useState(() => {
    const saved = localStorage.getItem('tokenExpiry');
    return saved ? parseInt(saved) : null;
  }); // Token expiration timestamp
  const [ssoError, setSsoError] = useState(null); // SSO error message state
  const [wsConnected, setWsConnected] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [users, setUsers] = useState(null); // For superadmin
  const [currentPage, setCurrentPage] = useState({}); // Pagination: current page per category
  const [usersPerPage] = useState(10); // Pagination: 10 users per page
  const [searchTerms, setSearchTerms] = useState({}); // Search terms for each user category
  const [inboxMessages, setInboxMessages] = useState([]); // For inbox - shown on dashboard
  const [inboxLoading, setInboxLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(null);
  const [updateDownloaded, setUpdateDownloaded] = useState(false);
  
  const websocketRef = useRef(null);
  const alertSoundIntervalRef = useRef(null);
  const alertTitleIntervalRef = useRef(null);
  // Persistent AudioContext — created & resumed inside a user gesture so it
  // stays in 'running' state. Browsers silently block resume() on a context
  // that was never unlocked inside a gesture handler.
  const audioContextRef = useRef(null);

  // Detect if running in desktop app
  useEffect(() => {
    if (window.electronAPI) {
      setIsDesktop(true);
      setAudioEnabled(true); // Desktop audio always works
      console.log('🖥️ Running in desktop app - reliable audio enabled');
      
      // Set up update listeners
      window.electronAPI.onUpdateAvailable((event, info) => {
        console.log('📥 Update available:', info);
        setUpdateInfo(info);
      });
      
      window.electronAPI.onUpdateProgress((event, progress) => {
        console.log('📊 Update progress:', progress);
        setUpdateProgress(progress);
      });
      
      window.electronAPI.onUpdateDownloaded((event, info) => {
        console.log('✅ Update downloaded:', info);
        setUpdateDownloaded(true);
        setUpdateProgress(null);
      });
      
      // Cleanup listeners on unmount
      return () => {
        if (window.electronAPI && window.electronAPI.removeUpdateListeners) {
          window.electronAPI.removeUpdateListeners();
        }
      };
    }
  }, []);

  // Simple audio enable on first interaction (web only)
  useEffect(() => {
    const isDesktopCheck = window.electronAPI ? true : false;
    if (isDesktopCheck) return; // Skip for desktop
    
    const enableAudio = async () => {
      if (!audioEnabled) {
        setAudioEnabled(true);
        console.log('🔊 Audio enabled after user interaction');
      }
      // Create and immediately resume the AudioContext INSIDE the gesture handler.
      // This is the only moment browsers guarantee resume() succeeds.
      // Once running it stays running for the lifetime of the page.
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
          console.log('🔊 AudioContext unlocked inside user gesture');
        }
      } catch (e) {
        console.warn('⚠️ AudioContext init failed:', e);
      }
    };
    
    // Listen for any user interaction to enable audio
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });
    document.addEventListener('touchstart', enableAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, [audioEnabled, isDesktop]);
  
  // Persist authentication state to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  useEffect(() => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
  }, [accessToken]);

  useEffect(() => {
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }, [refreshToken]);

  useEffect(() => {
    if (tokenExpiry) {
      localStorage.setItem('tokenExpiry', tokenExpiry.toString());
    } else {
      localStorage.removeItem('tokenExpiry');
    }
  }, [tokenExpiry]);
  
  // Initialize - restore session from localStorage
  useEffect(() => {
    console.log('App initialized - session restored from localStorage');
    if (user && token) {
      console.log('✅ Session restored:', user.email, 'Role:', user.role);
      
      // Check if token is expired or expiring soon
      if (tokenExpiry) {
        const now = Date.now();
        const timeUntilExpiry = tokenExpiry - now;
        
        if (timeUntilExpiry <= 0) {
          console.warn('⚠️ Token expired on app initialization, refreshing...');
          refreshAccessToken().then((success) => {
            if (success) {
              // Fetch data after successful refresh
              fetchInboxMessages(true);
              if (user.role === 'superadmin') {
                fetchUsers();
              }
            }
          });
          return; // Don't fetch data with expired token
        } else if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log('🔄 Token expiring soon on app initialization, refreshing...');
          refreshAccessToken();
        }
      }
      
      // Fetch inbox messages on session restore
      fetchInboxMessages(true);
      // Fetch users if superadmin
      if (user.role === 'superadmin') {
        console.log('👥 Fetching users for superadmin...');
        fetchUsers();
      }
    }
  }, []);

  // Fetch users when user logs in or token changes (for superadmins)
  useEffect(() => {
    if (user && user.role === 'superadmin' && token && !users) {
      console.log('👥 Auto-fetching users for superadmin after login');
      fetchUsers();
    }
  }, [user, token]);
  
  // Fetch unacknowledged alerts on login
  const fetchInboxOnLogin = async () => {
    await fetchInboxMessages(true);
  };

  // Fetch inbox on login and periodically
  const fetchInboxMessages = async (showAlert = false) => {
    setInboxLoading(true);
    try {
      console.log('🔑 fetchInboxMessages - Token value:', token ? `${token.substring(0, 20)}...` : 'NULL/UNDEFINED');
      console.log('🔑 Token type:', typeof token);
      if (!token) {
        console.error('❌ No token available for inbox request!');
        setInboxLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/inbox`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok && data.messages) {
        setInboxMessages(data.messages || []);
        if (showAlert && data.messages.length > 0) {
          // Show first unacknowledged alert
          const latestAlert = data.messages[0];
          handleNewAlert(latestAlert);
        }
      }
    } catch (error) {
      console.error('Error fetching inbox:', error);
    } finally {
      setInboxLoading(false);
    }
  };

  // Auto-refresh inbox every 10 seconds for real-time updates
  useEffect(() => {
    if (!user || !token) return;
    
    const interval = setInterval(() => {
      fetchInboxMessages(false);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [user, token]);

  // Auto-refresh live connections every 3 seconds for SuperAdmins (real-time online status)
  useEffect(() => {
    if (!user || !token || user.role !== 'superadmin') return;
    
    const interval = setInterval(() => {
      fetchLiveConnections();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [user, token, users]);

  // Auto-refresh access token before expiry
  useEffect(() => {
    if (!user || !tokenExpiry || !refreshToken) return;
    
    const checkTokenExpiry = () => {
      const now = Date.now();
      const timeUntilExpiry = tokenExpiry - now;
      
      // If token expired or expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 5 * 60 * 1000) {
        if (timeUntilExpiry <= 0) {
          console.warn('⚠️ Token expired, refreshing...');
        } else {
          console.log('🔄 Token expiring soon, refreshing...');
        }
        refreshAccessToken();
      }
    };
    
    // Check every minute
    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    
    // Check immediately
    checkTokenExpiry();
    
    return () => clearInterval(interval);
  }, [user, tokenExpiry, refreshToken]);

  // Check token expiration when window regains focus (after computer wakes from sleep)
  useEffect(() => {
    if (!user || !tokenExpiry || !refreshToken) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Window visible again, checking token expiration...');
        const now = Date.now();
        const timeUntilExpiry = tokenExpiry - now;
        
        if (timeUntilExpiry <= 0) {
          console.warn('⚠️ Token expired while window was inactive, refreshing...');
          refreshAccessToken();
        } else if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log('🔄 Token expiring soon after visibility change, refreshing...');
          refreshAccessToken();
        }
      }
    };

    const handleFocus = () => {
      console.log('🔍 Window focused, checking token expiration...');
      const now = Date.now();
      const timeUntilExpiry = tokenExpiry - now;
      
      if (timeUntilExpiry <= 0) {
        console.warn('⚠️ Token expired while window was inactive, refreshing...');
        refreshAccessToken();
      } else if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log('🔄 Token expiring soon after focus, refreshing...');
        refreshAccessToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, tokenExpiry, refreshToken]);

  // WebSocket connection
  useEffect(() => {
    if (user && accessToken) {
      if (!websocketRef.current) {
        connectWebSocket();
      }
    } else {
      // User logged out - close WebSocket immediately
      if (websocketRef.current) {
        console.log('🔌 Closing WebSocket - user logged out');
        websocketRef.current.close();
        websocketRef.current = null;
        setWsConnected(false);
      }
    }
    
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
    };
  }, [user, accessToken]);

  const connectWebSocket = () => {
    try {
      const ws = new WebSocket(WEBSOCKET_URL);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Authenticate the connection with access token
        if (accessToken) {
          console.log('📤 Sending WebSocket auth with token');
          ws.send(JSON.stringify({
            type: 'authenticate',
            token: accessToken // Use access token for WebSocket auth
          }));
        } else {
          console.warn('⚠️ No access token available for WebSocket authentication');
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('🔔 WebSocket message received:', data);
        console.log('🔔 Message type:', data.type);
        console.log('🔔 Full message data:', JSON.stringify(data, null, 2));

        switch (data.type) {
          case 'authenticated':
            console.log('✅ WebSocket authenticated successfully');
            break;
          case 'new_alert':
            console.log('🚨 NEW ALERT received via WebSocket:', data.alert);
            console.log('🚨 Alert data:', JSON.stringify(data.alert, null, 2));
            handleNewAlert(data.alert);
            break;
          case 'alert_acknowledged':
            console.log('✅ Alert acknowledged:', data);
            handleAlertAcknowledged(data);
            break;
          case 'auth_error':
            console.error('❌ ❌ ❌ WEBSOCKET AUTH ERROR - LOGGING OUT');
            console.error('Error message:', data.message);
            console.error('User role:', user?.role);
            console.error('User status:', user?.status);
            console.error('This usually means: user status is pending/rejected, token is invalid, or user not found');
            logout();
            break;
          default:
            console.log('❓ Unknown message type:', data.type, data);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        websocketRef.current = null;
        // Attempt to reconnect after 5 seconds
        if (user && accessToken) {
          setTimeout(connectWebSocket, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const handleNewAlert = (alert) => {
    console.log('🚨 handleNewAlert called with:', alert);
    console.log('👤 Current user:', user);
    console.log('👤 Current user role:', user?.role);
    
    // Guard: Ignore alerts if user is not logged in
    if (!user || !accessToken) {
      console.log('⚠️ Ignoring alert - user not logged in');
      return;
    }
    
    // Only show alert popup and sound for regular users (as per AI_CONTEXT.md design)
    if (user?.role === 'user') {
      // Validate if this alert is for the current user
      const isForThisUser = alert.targetType === user.stationType || 
                           (alert.targetType === 'specific' && alert.targetUserId === user.id);
      
      if (isForThisUser) {
        console.log('🚨 Showing alert popup and starting sound for user');
        setCurrentAlert(alert);
        startAlertSound();
      } else {
        console.log('⚠️ Alert received but not for this user - targetType:', alert.targetType, 'userStationType:', user.stationType);
      }
    } else {
      console.log('🚫 Alert received but no popup/sound for role:', user?.role, '(admins don\'t get popups)');
    }
  };

  const handleAlertAcknowledged = (data) => {
    // Silent acknowledgment - no popup for admins
    console.log(`✅ Alert acknowledged by ${data.acknowledgedBy?.username || 'unknown'}`);
    if (['admin', 'superadmin'].includes(user.role)) {
    }
  };

  // Play Web Audio beep (used by both web and desktop)
  const playWebAudioBeep = async () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume if suspended (for Safari)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      console.log('🔊 Alert beep played at', new Date().toLocaleTimeString());
      
      // Clean up
      setTimeout(() => {
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close();
        }
      }, 600);
    } catch (error) {
      console.error('❌ Web Audio beep failed:', error);
    }
  };

  const startAlertSound = async () => {
    // Only play sound for users with role 'user' (not admin/superadmin)
    if (user?.role !== 'user') {
      console.log('🚫 Sound blocked - user role is:', user?.role, '(only regular users get sound)');
      return;
    }
    
    // Desktop app: Bring window to front
    if (window.electronAPI) {
      console.log('🖥️ Desktop: Bringing window to front');
      try {
        await window.electronAPI.startAlertSound();
      } catch (error) {
        console.error('❌ Desktop window focus failed:', error);
      }
    }
    
    // Web browser: bring page to view and notify regardless of audioEnabled state
    if (!window.electronAPI) {
      // Attempt to bring the browser tab into focus
      window.focus();

      // Flash the document title so the user notices even if the tab is in the background
      const originalTitle = document.title;
      let titleFlashState = false;
      if (alertTitleIntervalRef.current) clearInterval(alertTitleIntervalRef.current);
      alertTitleIntervalRef.current = setInterval(() => {
        document.title = titleFlashState ? '🚨 EMERGENCY ALERT' : originalTitle;
        titleFlashState = !titleFlashState;
      }, 800);

      // Show a browser notification so the OS brings the tab to the foreground when clicked.
      // Permission is requested at login via requestPermissions().
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('🚨 EMERGENCY ALERT', {
          body: 'An emergency alert requires your immediate attention!',
          requireInteraction: true,
          tag: 'sos-alert',
          silent: false
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      }

      // If the user has not yet interacted with the page, audio context may be
      // suspended — mark it enabled now and attempt to resume so the beep fires.
      if (!audioEnabled) {
        setAudioEnabled(true);
      }
    }
    
    // Create audio context for beeping sound
    if (alertSoundIntervalRef.current) {
      console.log('🔊 Sound already playing, not starting new interval');
      return; // Already playing
    }

    // Web browser: Play beep using Web Audio API
    const playBeep = async () => {
      try {
        // Reuse the persistent AudioContext unlocked on first user interaction.
        // Creating a new context each time means it starts 'suspended' and
        // resume() silently fails outside a gesture — producing no sound.
        let ctx = audioContextRef.current;
        if (!ctx) {
          ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioContextRef.current = ctx;
        }
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.5);
        
        console.log('🔊 Alert beep played at', new Date().toLocaleTimeString());
      } catch (error) {
        console.error('❌ Web Audio beep failed:', error);
      }
    };
    
    // Play first beep immediately
    console.log('🔊 Starting alert sound for user role:', user?.role);
    playBeep();
    
    // Then play every 2 seconds
    alertSoundIntervalRef.current = setInterval(async () => {
      // Check current state via DOM/global - closure has stale values
      // We need to check if WebSocket is still connected as proxy for logged-in state
      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        console.log('🔇 Stopping alert sound - WebSocket disconnected (user likely logged out)');
        if (alertSoundIntervalRef.current) {
          clearInterval(alertSoundIntervalRef.current);
          alertSoundIntervalRef.current = null;
        }
        return;
      }
      console.log('🔊 Playing scheduled beep...');
      await playBeep();
    }, 2000);
    
    console.log('🔊 Alert sound interval started - will beep every 2 seconds');
  };

  const stopAlertSound = async () => {
    // Restore document title if it was flashing (web only)
    if (alertTitleIntervalRef.current) {
      clearInterval(alertTitleIntervalRef.current);
      alertTitleIntervalRef.current = null;
      document.title = 'SOS Alert System';
    }

    // Desktop app: Stop window flashing
    if (window.electronAPI) {
      try {
        await window.electronAPI.stopAlertSound();
        console.log('🔇 Desktop alert UI stopped');
      } catch (error) {
        console.error('❌ Error stopping desktop UI:', error);
      }
    }
    
    // Stop sound interval (both web and desktop)
    if (alertSoundIntervalRef.current) {
      clearInterval(alertSoundIntervalRef.current);
      alertSoundIntervalRef.current = null;
      console.log('🔇 Alert sound stopped');
    }
  };

  const acknowledgeAlert = async () => {
    if (!currentAlert) return;

    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${currentAlert.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        stopAlertSound();
        setCurrentAlert(null);
        console.log('✅ Alert acknowledged successfully');
      }
    } catch (error) {
      console.error('❌ Error acknowledging alert:', error);
    }
  };

  const refreshAccessToken = async () => {
    if (!refreshToken) {
      console.error('❌ No refresh token available');
      logout();
      return false;
    }

    try {
      console.log('🔄 Refreshing access token...');
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'refresh',
          refreshToken: refreshToken 
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.token) {
        setToken(data.token);
        setAccessToken(data.accessToken);
        if (data.refreshToken) {
          setRefreshToken(data.refreshToken);
        }
        setTokenExpiry(Date.now() + 55 * 60 * 1000); // Reset expiry to 55 minutes from now
        console.log('✅ Token refreshed successfully');
        return true;
      } else {
        console.error('❌ Token refresh failed:', data.error);
        logout();
        return false;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      logout();
      return false;
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      // Sign in through custom auth Lambda (which validates with Cognito)
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email, password }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Check if user needs to change password (temporary password flow)
        if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          return { 
            success: false, 
            requiresPasswordChange: true,
            session: data.session,
            email: data.email,
            message: data.message
          };
        }
        
        // Store the ID token for API calls and access token for WebSocket
        setToken(data.token); // ID token
        setAccessToken(data.accessToken); // Access token
        setRefreshToken(data.refreshToken); // Refresh token
        setTokenExpiry(Date.now() + 55 * 60 * 1000); // Set expiry to 55 minutes from now (5 min buffer)
        setUser(data.user);
        
        // Request audio and notification permissions for alert system
        if (data.user.role === 'user') {
          requestPermissions();
        }
        
        return { success: true };
      } else {
        console.error('❌ Login failed:', data.error || 'Login failed');
        return { success: false, error: data.error || data.message };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Login failed - check your connection' };
    } finally {
      setIsLoading(false);
    }
  };

  const completeNewPassword = async (email, session, newPassword) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'completeNewPassword', 
          email, 
          session, 
          newPassword 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Store tokens and user info
        setToken(data.token);
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        setTokenExpiry(Date.now() + 55 * 60 * 1000);
        setUser(data.user);
        
        // Request permissions for alert system
        if (data.user.role === 'user') {
          requestPermissions();
        }
        
        return { success: true, message: data.message };
      } else {
        console.error('❌ Password change failed:', data.error);
        return { success: false, error: data.error || data.message };
      }
    } catch (error) {
      console.error('❌ Password change error:', error);
      return { success: false, error: 'Password change failed - check your connection' };
    } finally {
      setIsLoading(false);
    }
  };

  // Handle SSO login redirect
  const handleSSOLogin = async (providerName) => {
    if (!SSO_CONFIG.cognitoDomain) {
      console.error('SSO is not properly configured');
      setSsoError('SSO is not properly configured. Please contact your administrator.');
      return;
    }

    // Check if running in Electron (desktop app)
    const isElectron = window.electronAPI !== undefined;
    
    if (isElectron) {
      // Desktop app: Use Electron's SSO flow with external browser
      try {
        console.log('🔐 Starting Electron SSO login for:', providerName);
        const result = await window.electronAPI.startSSOLogin(providerName, {
          cognitoDomain: SSO_CONFIG.cognitoDomain,
          region: COGNITO_CONFIG.region,
          clientId: COGNITO_CONFIG.userPoolClientId
        });
        
        if (result.success) {
          // Exchange code for tokens
          const tokenResponse = await fetch(`${API_BASE_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'sso_callback',
              code: result.code,
              redirectUri: result.redirectUri
            }),
          });

          const data = await tokenResponse.json();
          
          if (tokenResponse.ok && data.token) {
            setToken(data.token);
            setAccessToken(data.accessToken);
            setRefreshToken(data.refreshToken);
            setTokenExpiry(Date.now() + 55 * 60 * 1000);
            setUser(data.user);
            
            if (data.user.role === 'user') {
              requestPermissions();
            }
          } else {
            throw new Error(data.error || 'SSO authentication failed');
          }
        } else {
          throw new Error(result.error || 'SSO login cancelled');
        }
      } catch (error) {
        console.error('❌ Electron SSO error:', error);
        setSsoError(error.message || 'SSO login failed');
      }
      return;
    }

    // Web app: Continue with OAuth flow
    const redirectUri = window.location.origin; // Use root domain as callback
    const state = Math.random().toString(36).substring(7); // CSRF protection
    
    // Store state for validation on callback (use localStorage for persistence across redirects)
    localStorage.setItem('oauth_state', state);
    localStorage.setItem('oauth_provider', providerName);
    console.log('💾 Saved OAuth state:', state);
    
    // Build OAuth authorization URL
    const authUrl = `https://${SSO_CONFIG.cognitoDomain}.auth.${COGNITO_CONFIG.region}.amazoncognito.com/oauth2/authorize?` +
      `client_id=${COGNITO_CONFIG.userPoolClientId}&` +
      `response_type=code&` +
      `scope=openid+email+profile&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `identity_provider=${providerName}&` +
      `state=${state}`;
    
    console.log('🔐 Redirecting to SSO login:', providerName);
    window.location.href = authUrl;
  };
  
  // Check for OAuth callback on mount
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      
      // Not an OAuth callback
      if (!code && !error) return;
      
      // Handle OAuth error
      if (error) {
        console.error('❌ OAuth error:', error);
        const errorDescription = urlParams.get('error_description');
        setSsoError(`SSO Login failed: ${errorDescription || error}`);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Validate state (CSRF protection)
      const savedState = localStorage.getItem('oauth_state');
      console.log('🔍 State validation:', { receivedState: state, savedState: savedState, match: state === savedState });
      if (state !== savedState) {
        console.error('❌ Invalid OAuth state');
        console.error('  Received state:', state);
        console.error('  Saved state:', savedState);
        // Clear any stale session data
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_provider');
        setSsoError('SSO Login failed: Security validation failed. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      const provider = localStorage.getItem('oauth_provider');
      localStorage.removeItem('oauth_state');
      localStorage.removeItem('oauth_provider');
      
      // Exchange code for tokens via API
      setIsLoading(true);
      try {
        const redirectUri = window.location.origin; // Must match the one sent to Cognito
        const response = await fetch(`${API_BASE_URL}/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'sso_callback', 
            code, 
            redirectUri,
            provider 
          }),
        });

        const data = await response.json();
        console.log('🔍 SSO callback response:', { status: response.status, hasToken: !!data.token, hasAccessToken: !!data.accessToken, hasUser: !!data.user, userRole: data.user?.role });
        
        if (response.ok && data.token) {
          console.log('✅ SSO login successful');
          console.log('✅ User data:', data.user);
          console.log('✅ Tokens received - ID token:', !!data.token, 'Access token:', !!data.accessToken);
          
          // Store tokens
          setToken(data.token);
          setAccessToken(data.accessToken);
          setRefreshToken(data.refreshToken);
          setTokenExpiry(Date.now() + 55 * 60 * 1000);
          setUser(data.user);
          
          console.log('✅ State updates called - token set:', !!data.token, 'user set:', !!data.user);
          
          // Request permissions for users
          if (data.user.role === 'user') {
            requestPermissions();
          }
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          console.error('❌ SSO callback failed:', data.error);
          // Show user-friendly messages for specific errors
          if (data.error === 'Account pending approval') {
            setSsoError('PENDING_APPROVAL|Your account has been created and is awaiting administrator approval. You will receive an email notification once your access is granted.');
          } else {
            setSsoError(`SSO Login failed: ${data.error || 'Unknown error'}`);
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error('❌ SSO callback error:', error);
        setSsoError('SSO Login failed. Please check your connection and try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
      } finally {
        setIsLoading(false);
      }
    };
    
    handleOAuthCallback();
  }, []);
  
  const requestPermissions = async () => {
    try {
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        console.log('🔔 Notification permission:', permission);
      }
      
      // Initialize audio context with user gesture (helps with autoplay policy)
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          // This user interaction allows us to resume audio later
          document.addEventListener('click', async () => {
            if (audioContext.state === 'suspended') {
              await audioContext.resume();
              console.log('🔊 Audio context initialized after user interaction');
            }
          }, { once: true });
        }
        console.log('🔊 Audio context ready for alerts');
      } catch (audioError) {
        console.error('❌ Audio context initialization failed:', audioError);
      }
    } catch (error) {
      console.error('❌ Permission request failed:', error);
    }
  };

  const register = async (username, email, password, stationType) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'register', 
          username, 
          email, 
          password, 
          stationType 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // New workflow: users are pending approval, so no immediate login
        console.log('✅ Registration successful - pending approval');
        // Error/success will be shown in LoginPage component
        return { success: true, message: data.message };
      } else {
        console.error('❌ Registration failed:', data.error || 'Registration failed');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      return { success: false, error: 'Registration failed - check your connection' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    console.log('🔓 Logging out user');
    setIsLoading(true); // Show loading indicator
    
    // Revoke tokens on the backend and get Cognito logout URL
    if (accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/auth`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            action: 'logout',
            accessToken: accessToken 
          }),
        });
        
        const data = await response.json();
        console.log('✅ Token revoked on backend');
        
        // Clear all localStorage BEFORE clearing state (to avoid race conditions)
        localStorage.clear(); // Clear everything
        
        // Clear all client-side state
        setUser(null);
        setToken(null);
        setAccessToken(null);
        setRefreshToken(null);
        setTokenExpiry(null);
        setCurrentAlert(null);
        stopAlertSound();
        
        // Close WebSocket connection
        if (websocketRef.current) {
          websocketRef.current.close();
          websocketRef.current = null;
        }
        
        console.log('✅ Logout complete');
        
        // Redirect to Cognito logout URL to clear session cookies
        if (data.logoutUrl) {
          console.log('🔄 Redirecting to Cognito logout to clear session...');
          window.location.href = data.logoutUrl;
        }
      } catch (error) {
        console.error('⚠️ Failed to revoke token on backend:', error);
        // Continue with logout even if backend call fails
        
        // Clear all localStorage BEFORE clearing state
        localStorage.clear();
        
        // Clear all client-side state
        setUser(null);
        setToken(null);
        setAccessToken(null);
        setRefreshToken(null);
        setTokenExpiry(null);
        setCurrentAlert(null);
        stopAlertSound();
        
        // Close WebSocket connection
        if (websocketRef.current) {
          websocketRef.current.close();
          websocketRef.current = null;
        }
        
        console.log('✅ Logout complete (offline mode)');
      }
    } else {
      // No access token, just clear everything
      localStorage.clear();
      
      setUser(null);
      setToken(null);
      setAccessToken(null);
      setRefreshToken(null);
      setTokenExpiry(null);
      setCurrentAlert(null);
      stopAlertSound();
      
      // Close WebSocket connection
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
      
      console.log('✅ Logout complete (no token)');
      setIsLoading(false);
    }
  };

  const createAlert = async (message, targetType, targetUserId = null) => {
    console.log('📢 CREATING ALERT:');
    console.log('- Message:', message);
    console.log('- Target Type:', targetType);
    console.log('- Target User ID:', targetUserId);
    console.log('- Current User:', user);
    
    try {
      const alertData = { message, targetType, targetUserId };
      console.log('📢 Sending alert data:', JSON.stringify(alertData, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(alertData),
      });

      console.log('📢 Alert API response status:', response.status);
      const data = await response.json();
      console.log('📢 Alert API response data:', JSON.stringify(data, null, 2));
      
      if (response.ok) {
        console.log('✅ Alert sent successfully!');
        // Success - no popup needed
      } else {
        console.error('❌ Alert sending failed:', data);
      }
    } catch (error) {
      console.error('❌ Error sending alert:', error);
    }
  };

  const fetchUsers = async () => {
    console.log('🔍 fetchUsers called - user:', !!user, 'role:', user?.role, 'token exists:', !!token);
    
    if (!user || user.role !== 'superadmin' || !token) {
      console.log('⚠️ fetchUsers skipped - missing required data');
      return;
    }
    
    console.log('📋 Fetching users with token:', token.substring(0, 20) + '...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('✅ Users fetched successfully:', data.total, 'total users');
        setUsers(data.users);
        setCurrentPage(1); // Reset to first page when fetching new data
      } else {
        console.error('❌ Failed to fetch users:', data.error);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
    }
  };

  // Helper function to paginate user arrays
  const paginateUsers = (userArray, categoryKey, currentPageState) => {
    if (!userArray || userArray.length === 0) return [];
    const page = currentPageState[categoryKey] || 1;
    const indexOfLastUser = page * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return userArray.slice(indexOfFirstUser, indexOfLastUser);
  };

  // Calculate total pages for current category
  const getTotalPages = (userArray, categoryKey) => {
    if (!userArray || userArray.length === 0) return 0;
    return Math.ceil(userArray.length / usersPerPage);
  };

  // Update pagination info text
  const getPaginationInfo = (userArray, categoryKey, currentPageState) => {
    if (!userArray || userArray.length === 0) return 'No users';
    const page = currentPageState[categoryKey] || 1;
    const totalPages = getTotalPages(userArray, categoryKey);
    const startIndex = (page - 1) * usersPerPage + 1;
    const endIndex = Math.min(page * usersPerPage, userArray.length);
    return `Showing ${startIndex}-${endIndex} of ${userArray.length}`;
  };

  // Pagination controls component
  const PaginationControls = ({ userArray, category }) => {
    const page = currentPage[category] || 1;
    const totalPages = getTotalPages(userArray, category);
    if (totalPages <= 1) return null;

    return (
      <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <button
          onClick={() => setCurrentPage(prev => ({ ...prev, [category]: Math.max((prev[category] || 1) - 1, 1) }))}
          disabled={page === 1}
          style={{
            padding: '10px 20px',
            marginRight: '10px',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            opacity: page === 1 ? 0.5 : 1,
            backgroundColor: page === 1 ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          ← Previous
        </button>
        <span style={{ margin: '0 15px', fontWeight: 'bold', fontSize: '14px' }}>
          Page {page} of {totalPages} | {getPaginationInfo(userArray, category, currentPage)}
        </span>
        <button
          onClick={() => setCurrentPage(prev => ({ ...prev, [category]: Math.min((prev[category] || 1) + 1, totalPages) }))}
          disabled={page === totalPages}
          style={{
            padding: '10px 20px',
            marginLeft: '10px',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
            opacity: page === totalPages ? 0.5 : 1,
            backgroundColor: page === totalPages ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          Next →
        </button>
      </div>
    );
  };

  // Fetch live connection data to show real-time online status
  const fetchLiveConnections = async () => {
    if (user.role !== 'superadmin' || !users) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/connections`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok && data.connections) {
        // Map active connections by userId for easy lookup
        const activeConnections = new Map();
        data.connections.forEach(conn => {
          if (conn.userId) {
            activeConnections.set(conn.userId, conn);
          }
        });
        
        // Update users to show current online status based on active connections
        const updatedUsers = {
          ...users,
          superadmins: users.superadmins.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          admins: users.admins.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          nbcu_users: users.nbcu_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          telemundo_users: users.telemundo_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          pending_users: users.pending_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          rejected_users: users.rejected_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
        };
        setUsers(updatedUsers);
        console.log(`🟢 Updated online status for ${activeConnections.size} connected users`);
      }
    } catch (error) {
      console.error('Error fetching live connections:', error);
    }
  };

  const updateUserRole = async (userId, newRole, newStationType) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newRole, newStationType }),
      });

      if (response.ok) {
        console.log('✅ User updated successfully');
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to update user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error updating user:', error);
    }
  };

  const approveUser = async (userId) => {
    // Direct approval - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User approved successfully');
        await fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to approve user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error approving user:', error);
    }
  };

  const rejectUser = async (userId) => {
    // Direct rejection - no confirmation dialog
    try{
      const response = await fetch(`${API_BASE_URL}/users/${userId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User rejected successfully');
        await fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to reject user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error rejecting user:', error);
    }
  };

  const deleteUser = async (userId) => {
    // Direct deletion - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User deleted successfully');
        await fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to delete user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    // Direct deletion - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        console.log('✅ Message deleted successfully');
        await fetchInboxMessages(false); // Refresh inbox
      } else {
        const data = await response.json();
        console.error('❌ Failed to delete message:', data.error);
      }
    } catch (error) {
      console.error('❌ Error deleting message:', error);
    }
  };

  // Render components based on authentication state
  if (!user) {
    return <LoginPage 
      onLogin={login} 
      onRegister={register} 
      onCompleteNewPassword={completeNewPassword}
      isLoading={isLoading} 
      handleSSOLogin={handleSSOLogin}
      ssoError={ssoError}
      onClearSsoError={() => setSsoError(null)}
    />;
  }

  return (
    <div className="container">
      {/* Loading Overlay for Logout */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{
            color: 'white',
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: '600',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            Logging out...
          </div>
        </div>
      )}
      
      <ConnectionStatus connected={wsConnected} />
      
      {/* Header */}
      <div className="card" style={{
        background: '#ffffff',
        borderBottom: '1px solid #e8eaed',
        color: '#1f2937',
        borderRadius: '0',
        padding: '20px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>SOS Alert System</h1>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '13px' }}>{user.email} • <span style={{ fontWeight: '500', color: '#1f2937' }}>{user.role === 'superadmin' ? 'Super Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</span></p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: wsConnected ? '#059669' : '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: wsConnected ? '#059669' : '#ef4444', display: 'inline-block' }}></span>
                {wsConnected ? 'Connected' : 'Offline'}
              </div>
              {user.stationType && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{user.stationType} Station</div>}
            </div>
            <button className="button" onClick={logout} style={{ 
              background: '#f3f4f6', 
              border: '1px solid #d1d5db',
              color: '#1f2937',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s',
              hover: { background: '#e5e7eb' }
            }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop App Update Notifications */}
      {isDesktop && (updateInfo || updateProgress || updateDownloaded) && (
        <UpdateNotification 
          updateInfo={updateInfo}
          updateProgress={updateProgress}
          updateDownloaded={updateDownloaded}
          onDismiss={() => {
            setUpdateInfo(null);
            setUpdateProgress(null);
            setUpdateDownloaded(false);
          }}
        />
      )}
      
      {currentAlert && (
        <AlertPopup 
          alert={currentAlert} 
          onAcknowledge={acknowledgeAlert}
          userRole={user.role}
        />
      )}

      {/* Banner shown to 'user' role when browser notification permission isn't granted.
          Without this, alerts arrive silently and the tab doesn't pop to front. */}
      {user?.role === 'user' && !isDesktop && 'Notification' in window && Notification.permission !== 'granted' && (
        <div style={{
          position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          background: '#b91c1c', color: '#fff', padding: '10px 20px', borderRadius: 8,
          zIndex: 9999, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          fontSize: 14, maxWidth: '90vw'
        }}>
          <span>⚠️ <strong>Alert notifications are blocked.</strong> Click below to enable sound &amp; pop-up alerts.</span>
          <button
            onClick={async () => {
              // Request permission inside a click gesture — the only time browsers allow it
              const perm = await Notification.requestPermission();
              console.log('🔔 Notification permission after prompt:', perm);
              // Also unlock AudioContext in the same gesture
              try {
                if (!audioContextRef.current) {
                  audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                if (audioContextRef.current.state === 'suspended') {
                  await audioContextRef.current.resume();
                }
                setAudioEnabled(true);
              } catch(e) { console.warn(e); }
            }}
            style={{
              background: '#fff', color: '#b91c1c', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
            }}
          >
            Enable Alerts
          </button>
        </div>
      )}

      {/* Main Content */}
      {user.role === 'user' && <UserDashboard messages={inboxMessages} loading={inboxLoading} />}
      
      {['admin', 'superadmin'].includes(user.role) && (
        <AdminDashboard 
          onCreateAlert={createAlert}
          userRole={user.role}
          users={users}
          onFetchUsers={fetchUsers}
          onUpdateUser={updateUserRole}
          onApproveUser={approveUser}
          onRejectUser={rejectUser}
          onDeleteUser={deleteUser}
          token={token}
          inboxMessages={inboxMessages}
          inboxLoading={inboxLoading}
          onDeleteMessage={deleteMessage}
          onRefreshInbox={() => fetchInboxMessages(false)}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          usersPerPage={usersPerPage}
          searchTerms={searchTerms}
          setSearchTerms={setSearchTerms}
          paginateUsers={paginateUsers}
          getTotalPages={getTotalPages}
          getPaginationInfo={getPaginationInfo}
          PaginationControls={PaginationControls}
        />
      )}
    </div>
  );
}

// Connection status indicator
function ConnectionStatus({ connected }) {
  return (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      {connected ? '🟢 Connected' : '🔴 Disconnected'}
    </div>
  );
}

// Update notification for desktop app
function UpdateNotification({ updateInfo, updateProgress, updateDownloaded, onDismiss }) {
  const getNotificationStyle = () => {
    if (updateDownloaded) {
      return {
        backgroundColor: '#d4edda',
        borderColor: '#c3e6cb',
        color: '#155724'
      };
    } else if (updateProgress) {
      return {
        backgroundColor: '#fff3cd',
        borderColor: '#ffeaa7',
        color: '#856404'
      };
    } else {
      return {
        backgroundColor: '#d1ecf1',
        borderColor: '#bee5eb',
        color: '#0c5460'
      };
    }
  };

  const getNotificationContent = () => {
    if (updateDownloaded) {
      return {
        icon: '✅',
        title: 'Update Ready!',
        message: 'App will restart in 5 seconds to apply the update.'
      };
    } else if (updateProgress) {
      return {
        icon: '📥',
        title: 'Downloading Update...',
        message: `${Math.round(updateProgress.percent)}% complete (${Math.round(updateProgress.bytesPerSecond / 1024)} KB/s)`
      };
    } else if (updateInfo) {
      return {
        icon: '🆕',
        title: 'Update Available',
        message: `Version ${updateInfo.version} is downloading in the background.`
      };
    }
  };

  const content = getNotificationContent();
  if (!content) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 10000,
      padding: '15px 20px',
      border: '1px solid',
      borderRadius: '8px',
      minWidth: '300px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      ...getNotificationStyle()
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', marginRight: '10px' }}>{content.icon}</span>
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{content.title}</div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>{content.message}</div>
          </div>
        </div>
        {!updateDownloaded && (
          <button 
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              opacity: 0.7,
              marginLeft: '10px'
            }}
          >
            ×
          </button>
        )}
      </div>
      
      {updateProgress && (
        <div style={{ marginTop: '10px' }}>
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${updateProgress.percent}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Professional Alert Modal Popup
function AlertPopup({ alert, onAcknowledge, userRole }) {
  if (userRole !== 'user' || !alert) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s ease',
        border: '4px solid #ef4444'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 1s infinite' }}>🚨</div>
          <h2 style={{ margin: '0', color: '#1f2937', fontSize: '24px', fontWeight: '700' }}>URGENT ALERT</h2>
        </div>
        
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
          <p style={{ margin: '0', color: '#1f2937', fontSize: '16px', lineHeight: '1.6' }}>
            {alert.message}
          </p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px', fontSize: '13px', color: '#666' }}>
          <div>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>From:</span><br />
            {alert.createdByUsername}
          </div>
          <div>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>Time:</span><br />
            {new Date(alert.createdAt).toLocaleTimeString()}
          </div>
        </div>
        
        <button 
          className="button success" 
          onClick={onAcknowledge}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          ✅ ACKNOWLEDGE ALERT
        </button>
      </div>
      
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// User Dashboard with integrated inbox
function UserDashboard({ messages = [], loading = false }) {
  const [expanded, setExpanded] = useState(false);
  
  const getTargetDisplay = (msg) => {
    if (msg.targetType === 'specific') return '🎯 Specific User';
    if (msg.targetType === 'NBCU') return '🏢 NBCU Station';
    return '🌐 Telemundo Station';
  };
  
  const getTimeRemaining = (createdAt) => {
    const expiryTime = createdAt + (24 * 60 * 60 * 1000);
    const now = Date.now();
    const remaining = expiryTime - now;
    
    if (remaining <= 0) return 'Expired';
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };
  
  const renderMessage = (msg, isPreview = false) => (
    <div key={`${msg.id}`} style={{
      borderLeft: '4px solid #3b82f6',
      padding: '16px',
      marginBottom: '12px',
      borderRadius: '8px',
      background: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: '0', fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>
            From: {msg.createdByUsername}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
            {new Date(msg.createdAt).toLocaleString()}
          </p>
        </div>
        {!isPreview && <span style={{ fontSize: '11px', fontWeight: '600', color: '#10b981' }}>✓</span>}
      </div>
      
      {/* Target and Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Target:</span><br />
          <span style={{ color: '#1f2937' }}>{getTargetDisplay(msg)}</span>
        </div>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Expires:</span><br />
          <span style={{ color: msg.createdAt + (24 * 60 * 60 * 1000) - Date.now() < 1 * 60 * 60 * 1000 ? '#ef4444' : '#1f2937' }}>
            {getTimeRemaining(msg.createdAt)}
          </span>
        </div>
      </div>
      
      {/* Message */}
      <div style={{
        padding: '12px',
        background: '#f3f4f6',
        borderRadius: '6px',
        marginBottom: '12px',
        color: '#374151',
        fontSize: '14px',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: isPreview ? '100px' : 'none',
        overflow: isPreview ? 'hidden' : 'visible'
      }}>
        {msg.message}
      </div>
    </div>
  );
  
  const displayedMessages = expanded ? messages : messages.slice(0, 2);
  
  return (
    <div>
      {/* Messages Header */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
        cursor: messages.length > 2 ? 'pointer' : 'default',
        transition: 'all 0.2s'
      }}
      onClick={() => messages.length > 2 && setExpanded(!expanded)}
      onMouseOver={(e) => messages.length > 2 && (e.target.style.opacity = '0.9')}
      onMouseOut={(e) => messages.length > 2 && (e.target.style.opacity = '1')}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>📬 Inbox</h2>
            <p style={{ margin: '0', opacity: 0.9, fontSize: '13px' }}>
              {messages.length} message{messages.length !== 1 ? 's' : ''} • {expanded ? 'Showing all' : `Showing ${Math.min(2, messages.length)} of ${messages.length}`}
            </p>
          </div>
          {messages.length > 2 && (
            <button style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              {expanded ? '▼ Collapse' : '▶ View All'}
            </button>
          )}
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="card" style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
          Loading messages...
        </div>
      )}
      
      {/* Empty State */}
      {!loading && messages.length === 0 && (
        <div className="card" style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#999',
          borderRadius: '12px',
          background: '#f9fafb'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
          <p style={{ margin: '0', fontSize: '16px', fontWeight: '500' }}>No messages</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>You're all caught up!</p>
        </div>
      )}
      
      {/* Messages List */}
      {!loading && messages.length > 0 && (
        <div>
          {displayedMessages.map((msg) => renderMessage(msg))}
        </div>
      )}
    </div>
  );
}

// Admin dashboard with alert creation and inbox integration
function AdminDashboard({ onCreateAlert, userRole, users, onFetchUsers, onUpdateUser, onApproveUser, onRejectUser, onDeleteUser, token, inboxMessages = [], inboxLoading = false, onDeleteMessage, onRefreshInbox, currentPage, setCurrentPage, usersPerPage, searchTerms, setSearchTerms, paginateUsers, getTotalPages, getPaginationInfo, PaginationControls }) {
  const [alertMessage, setAlertMessage] = useState('');
  const [targetType, setTargetType] = useState('NBCU');
  const [selectedUserId, setSelectedUserId] = useState('');

  const handleCreateAlert = () => {
    if (!alertMessage.trim()) {
      console.log('⚠️ Please enter a message');
      return;
    }

    if (targetType === 'specific' && !selectedUserId) {
      console.log('⚠️ Please select a user for specific alerts');
      return;
    }

    onCreateAlert(alertMessage, targetType, targetType === 'specific' ? selectedUserId : null);
    setAlertMessage('');
  };

  useEffect(() => {
    console.log('🔍 AdminDashboard useEffect - userRole:', userRole, 'token exists:', !!token);
    if (userRole === 'superadmin' && token) {
      console.log('✅ Calling fetchUsers');
      onFetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, token]);

  return (
    <div>
      {/* Alert Creation Card */}
      <div className="card" style={{
        borderTop: '4px solid #ef4444',
        padding: '24px',
        borderRadius: '12px',
        background: 'white',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1f2937' }}>📢 Send Alert</h2>
        <textarea
          className="textarea"
          value={alertMessage}
          onChange={(e) => setAlertMessage(e.target.value)}
          placeholder="Enter your alert message... (up to 3000 words, any characters allowed)"
          rows="5"
          style={{
            minHeight: '120px',
            maxHeight: '250px',
            resize: 'vertical',
            width: '100%',
            fontSize: '14px',
            lineHeight: '1.5',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        />
        <div style={{ 
          fontSize: '12px', 
          color: '#999', 
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <span>{alertMessage.length} chars</span>
          <span>{alertMessage.trim() ? alertMessage.trim().split(/\s+/).length : 0} words</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <select 
            className="select" 
            value={targetType} 
            onChange={(e) => setTargetType(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            <option value="NBCU">→ NBCU Station</option>
            <option value="Telemundo">→ Telemundo Station</option>
            {userRole === 'superadmin' && (
              <option value="specific">→ Specific User</option>
            )}
          </select>

          {targetType === 'specific' && users && (
            <select 
              className="select" 
              value={selectedUserId} 
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">Select user...</option>
              {[...users.nbcu_users, ...users.telemundo_users].map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} ({user.stationType})
                </option>
              ))}
            </select>
          )}
        </div>

        <button className="button" onClick={handleCreateAlert} style={{
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
        }}>
          📢 Send Alert Now
        </button>
      </div>

      {/* Admin Sent Messages - Same Expandable Layout as User Inbox */}
      {(userRole === 'admin' || userRole === 'superadmin') && (
        <AdminSentMessages 
          messages={inboxMessages}
          loading={inboxLoading}
          onRefresh={onRefreshInbox}
          onDelete={onDeleteMessage}
          token={token}
        />
      )}

      {userRole === 'superadmin' && (
        <SuperAdminPanel 
          users={users} 
          onUpdateUser={onUpdateUser}
          onRefresh={onFetchUsers}
          onApproveUser={onApproveUser}
          onRejectUser={onRejectUser}
          onDeleteUser={onDeleteUser}
          token={token}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          usersPerPage={usersPerPage}
          searchTerms={searchTerms}
          setSearchTerms={setSearchTerms}
          paginateUsers={paginateUsers}
          getTotalPages={getTotalPages}
          getPaginationInfo={getPaginationInfo}
          PaginationControls={PaginationControls}
        />
      )}
    </div>
  );
}

// Super admin user management panel
function SuperAdminPanel({ users, onUpdateUser, onRefresh, onApproveUser, onRejectUser, onDeleteUser, token, currentPage, setCurrentPage, usersPerPage, searchTerms, setSearchTerms, paginateUsers, getTotalPages, getPaginationInfo, PaginationControls }) {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    super_admins: true,
    admins: true,
    nbcu_users: true,
    telemundo_users: true,
    pending_users: false,  // Collapsed by default to draw attention
    rejected_users: false   // Collapsed by default
  });

  // User creation states
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    role: 'user',
    stationType: 'NBCU'
  });
  const [bulkUsers, setBulkUsers] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserResult, setCreateUserResult] = useState(null);
  const [bulkCreateResult, setBulkCreateResult] = useState(null);

  const fetchDomains = async () => {
    setLoadingDomains(true);
    try {
      const response = await fetch(`${API_BASE_URL}/domains`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      if (response.ok) {
        setDomains(data.domains || []);
      } else {
        console.error('❌ Failed to fetch domains:', data.error);
      }
    } catch (error) {
      console.error('❌ Error fetching domains:', error);
    } finally {
      setLoadingDomains(false);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) {
      console.log('⚠️ Please enter a domain');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/domains`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: newDomain.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log('✅ Domain added successfully');
        setNewDomain('');
        fetchDomains();
      } else {
        console.error('❌ Failed to add domain:', data.error);
      }
    } catch (error) {
      console.error('❌ Error adding domain:', error);
    }
  };

  const removeDomain = async (domain) => {
    // Direct deletion - no confirmation
    try {
      const response = await fetch(`${API_BASE_URL}/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      if (response.ok) {
        console.log('✅ Domain removed successfully');
        fetchDomains();
      } else {
        console.error('❌ Failed to remove domain:', data.error);
      }
    } catch (error) {
      console.error('❌ Error removing domain:', error);
    }
  };

  // Wrapper functions with loading states
  const handleApproveUser = async (userId) => {
    setProcessingUserId(userId);
    await onApproveUser(userId);
    setProcessingUserId(null);
  };

  const handleRejectUser = async (userId) => {
    setProcessingUserId(userId);
    await onRejectUser(userId);
    setProcessingUserId(null);
  };

  const handleDeleteUser = async (userId) => {
    setProcessingUserId(userId);
    await onDeleteUser(userId);
    setProcessingUserId(null);
  };

  // Create a single user manually
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.username) {
      alert('Please fill in all required fields');
      return;
    }

    setCreatingUser(true);
    setCreateUserResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });

      const data = await response.json();

      if (response.ok) {
        setCreateUserResult({
          success: true,
          message: 'User created successfully!',
          user: data.user,
          temporaryPassword: data.temporaryPassword
        });
        // Reset form
        setNewUser({
          email: '',
          username: '',
          role: 'user',
          stationType: 'NBCU'
        });
        // Refresh user list
        await onRefresh();
      } else {
        setCreateUserResult({
          success: false,
          message: data.error || 'Failed to create user'
        });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setCreateUserResult({
        success: false,
        message: 'Network error. Please try again.'
      });
    } finally {
      setCreatingUser(false);
    }
  };

  // Bulk create users from CSV
  const handleBulkUpload = async () => {
    if (!bulkUsers.trim()) {
      alert('Please paste CSV data');
      return;
    }

    setCreatingUser(true);
    setBulkCreateResult(null);

    try {
      // Parse CSV (simple parser - assumes: email,username,role,stationType)
      const lines = bulkUsers.trim().split('\n');
      const users = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.startsWith('#')) continue; // Skip empty lines and comments

        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 4) {
          console.warn(`Skipping invalid line ${i + 1}: ${line}`);
          continue;
        }

        // Skip header row (if first column is literally "email")
        if (i === 0 && parts[0].toLowerCase() === 'email') {
          console.log('Skipping CSV header row');
          continue;
        }

        users.push({
          email: parts[0],
          username: parts[1],
          role: parts[2],
          stationType: parts[3]
        });
      }

      if (users.length === 0) {
        alert('No valid users found in CSV data');
        setCreatingUser(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'bulkCreate',
          users: users
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setBulkCreateResult(data);
        setBulkUsers(''); // Clear input
        // Refresh user list
        await onRefresh();
      } else {
        setBulkCreateResult({
          summary: { total: 0, successful: 0, failed: users.length },
          results: { successful: [], failed: users.map(u => ({ email: u.email, error: data.error })) }
        });
      }
    } catch (error) {
      console.error('Error bulk uploading users:', error);
      alert('Network error. Please try again.');
    } finally {
      setCreatingUser(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDomains();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  if (!users) {
    return (
      <div className="card">
        <h2>User Management</h2>
        <button className="button" onClick={onRefresh}>Load Users</button>
      </div>
    );
  }

  const renderUserList = (userList, title) => {
    const categoryKey = title.toLowerCase().replace(/ /g, '_');
    const searchTerm = searchTerms[categoryKey] || '';
    const isExpanded = expandedSections[categoryKey];
    
    // Filter users based on search term
    const filteredUsers = searchTerm
      ? userList.filter(user => 
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : userList;
    
    const paginatedUsers = paginateUsers(filteredUsers, categoryKey, currentPage);
    
    return (
      <div style={{ marginBottom: '30px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
        <div 
          onClick={() => setExpandedSections(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }))}
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '15px',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>{isExpanded ? '▼' : '▶'}</span>
            {title} ({filteredUsers.length}{searchTerm ? ` of ${userList.length}` : ''})
          </h4>
        </div>
        {isExpanded && (
          <div style={{ padding: '15px' }}>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="text"
                placeholder={`🔍 Search ${title}...`}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerms(prev => ({ ...prev, [categoryKey]: e.target.value }));
                  setCurrentPage(prev => ({ ...prev, [categoryKey]: 1 }));
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
            </div>
            {filteredUsers.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                {searchTerm ? `No users found matching "${searchTerm}"` : 'No users in this category'}
              </p>
            ) : (
              <>
                <PaginationControls userArray={filteredUsers} category={categoryKey} />
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr', 
                  gap: '10px', 
                  padding: '10px', 
                  backgroundColor: '#f5f5f5', 
                  fontWeight: 'bold', 
                  borderRadius: '4px', 
                  marginBottom: '5px' 
                }}>
          <div>User</div>
          <div>Role</div>
          <div>Station Type</div>
          <div>Actions</div>
        </div>
        {paginatedUsers.map(user => (
        <div key={user.id} style={{ 
          display: 'grid', 
          gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr', 
          gap: '10px', 
          alignItems: 'center',
          border: '1px solid #ddd', 
          padding: '10px', 
          margin: '5px 0',
          borderRadius: '4px' 
        }}>
          <div>
            <strong>{user.username}</strong><br />
            <small style={{ color: '#666' }}>({user.email})</small>
            {/* User Status - Based on live connections */}
            <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: '500' }}>
              {user.isConnected ? (
                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span>
                  🟢 Online
                </span>
              ) : (
                <span style={{ color: '#d1d5db', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#d1d5db', display: 'inline-block' }}></span>
                  ⚫ Offline
                </span>
              )}
            </div>
          </div>
          
          <div>
            <select 
              defaultValue={user.role}
              onChange={(e) => onUpdateUser(user.id, e.target.value, user.stationType)}
              style={{ width: '100%', fontSize: '12px' }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>
          
          <div>
            <select 
              defaultValue={user.stationType}
              onChange={(e) => onUpdateUser(user.id, user.role, e.target.value)}
              style={{ width: '100%', fontSize: '12px' }}
            >
              <option value="NBCU">NBCU</option>
              <option value="Telemundo">Telemundo</option>
            </select>
          </div>
          
          <div>
            <button 
              className="button danger" 
              onClick={() => handleDeleteUser(user.id)}
              disabled={processingUserId === user.id}
              style={{ fontSize: '11px', padding: '5px 10px', width: '100%', cursor: processingUserId === user.id ? 'not-allowed' : 'pointer', opacity: processingUserId === user.id ? 0.5 : 1 }}
            >
              {processingUserId === user.id ? '⏳' : '🗑️'} Delete
            </button>
          </div>
        </div>
        ))}
                <PaginationControls userArray={filteredUsers} category={categoryKey} />
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPendingUserList = (userList, title) => {
    if (!userList || userList.length === 0) return null;
    
    const categoryKey = 'pending_users';
    const searchTerm = searchTerms[categoryKey] || '';
    const isExpanded = expandedSections[categoryKey];
    
    // Filter users based on search term
    const filteredUsers = searchTerm
      ? userList.filter(user => 
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : userList;
    
    const paginatedUsers = paginateUsers(filteredUsers, categoryKey, currentPage);
    
    return (
      <div style={{ marginTop: '20px', border: '2px solid #ffc107', borderRadius: '8px', overflow: 'hidden' }}>
        <div 
          onClick={() => setExpandedSections(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }))}
          style={{ 
            padding: '15px', 
            backgroundColor: '#fff3cd',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <h4 style={{ color: '#856404', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>{isExpanded ? '▼' : '▶'}</span>
            ⏳ {title} ({filteredUsers.length}{searchTerm ? ` of ${userList.length}` : ''})
          </h4>
        </div>
        {isExpanded && (
          <div style={{ padding: '15px', backgroundColor: '#fff3cd' }}>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="🔍 Search pending users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerms(prev => ({ ...prev, [categoryKey]: e.target.value }));
                  setCurrentPage(prev => ({ ...prev, [categoryKey]: 1 }));
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
            </div>
            <p style={{ fontSize: '13px', color: '#856404', marginBottom: '15px' }}>These users require approval before they can log in.</p>
        {filteredUsers.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            {searchTerm ? `No pending users found matching "${searchTerm}"` : 'No pending users'}
          </p>
        ) : (
          <>
            <PaginationControls userArray={filteredUsers} category="pending_users" />
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 2fr 1fr 2fr', 
          gap: '10px', 
          padding: '10px', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          fontWeight: 'bold', 
          borderRadius: '4px', 
          marginBottom: '5px' 
        }}>
          <div>Username</div>
          <div>Email</div>
          <div>Station Type</div>
          <div>Actions</div>
        </div>
        {paginatedUsers.map(user => (
          <div key={user.id} style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 2fr 1fr 2fr', 
            gap: '10px', 
            alignItems: 'center',
            border: '1px solid #ddd', 
            padding: '10px', 
            margin: '5px 0',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            <div><strong>{user.username}</strong></div>
            <div style={{ fontSize: '13px' }}>{user.email}</div>
            <div style={{ fontSize: '13px' }}>{user.stationType}</div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                className="button" 
                onClick={() => handleApproveUser(user.id)}
                disabled={processingUserId === user.id}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: processingUserId === user.id ? '#ccc' : '#28a745', flex: 1, cursor: processingUserId === user.id ? 'not-allowed' : 'pointer' }}
              >
                {processingUserId === user.id ? '⏳' : '✅'} Approve
              </button>
              <button 
                className="button danger" 
                onClick={() => handleRejectUser(user.id)}
                disabled={processingUserId === user.id}
                style={{ fontSize: '11px', padding: '5px 10px', flex: 1, cursor: processingUserId === user.id ? 'not-allowed' : 'pointer', opacity: processingUserId === user.id ? 0.5 : 1 }}
              >
                {processingUserId === user.id ? '⏳' : '❌'} Reject
              </button>
              <button 
                className="button danger" 
                onClick={() => handleDeleteUser(user.id)}
                disabled={processingUserId === user.id}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#dc3545', cursor: processingUserId === user.id ? 'not-allowed' : 'pointer', opacity: processingUserId === user.id ? 0.5 : 1 }}
              >
                {processingUserId === user.id ? '⏳' : '🗑️'}
              </button>
            </div>
          </div>
        ))}
            <PaginationControls userArray={filteredUsers} category="pending_users" />
          </>
        )}
          </div>
        )}
      </div>
    );
  };

  const renderRejectedUserList = (userList, title) => {
    if (!userList || userList.length === 0) return null;
    
    const categoryKey = 'rejected_users';
    const searchTerm = searchTerms[categoryKey] || '';
    const isExpanded = expandedSections[categoryKey];
    
    // Filter users based on search term
    const filteredUsers = searchTerm
      ? userList.filter(user => 
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : userList;
    
    const paginatedUsers = paginateUsers(filteredUsers, categoryKey, currentPage);
    
    return (
      <div style={{ marginTop: '20px', border: '2px solid #dc3545', borderRadius: '8px', overflow: 'hidden' }}>
        <div 
          onClick={() => setExpandedSections(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }))}
          style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <h4 style={{ color: '#721c24', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>{isExpanded ? '▼' : '▶'}</span>
            🚫 {title} ({filteredUsers.length}{searchTerm ? ` of ${userList.length}` : ''})
          </h4>
        </div>
        {isExpanded && (
          <div style={{ padding: '15px', backgroundColor: '#f8d7da' }}>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="🔍 Search rejected users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerms(prev => ({ ...prev, [categoryKey]: e.target.value }));
                  setCurrentPage(prev => ({ ...prev, [categoryKey]: 1 }));
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
            </div>
            <p style={{ fontSize: '13px', color: '#721c24', marginBottom: '15px' }}>These users have been rejected and cannot log in.</p>
        {filteredUsers.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            {searchTerm ? `No rejected users found matching "${searchTerm}"` : 'No rejected users'}
          </p>
        ) : (
          <>
            <PaginationControls userArray={filteredUsers} category="rejected_users" />
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 2fr 1fr 2fr', 
          gap: '10px', 
          padding: '10px', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          fontWeight: 'bold', 
          borderRadius: '4px', 
          marginBottom: '5px' 
        }}>
          <div>Username</div>
          <div>Email</div>
          <div>Station Type</div>
          <div>Actions</div>
        </div>
        {paginatedUsers.map(user => (
          <div key={user.id} style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 2fr 1fr 2fr', 
            gap: '10px', 
            alignItems: 'center',
            border: '1px solid #ddd', 
            padding: '10px', 
            margin: '5px 0',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            <div><strong>{user.username}</strong></div>
            <div style={{ fontSize: '13px' }}>{user.email}</div>
            <div style={{ fontSize: '13px' }}>{user.stationType}</div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                className="button" 
                onClick={() => handleApproveUser(user.id)}
                disabled={processingUserId === user.id}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#28a745', flex: 1, cursor: processingUserId === user.id ? 'not-allowed' : 'pointer', opacity: processingUserId === user.id ? 0.5 : 1 }}
              >
                {processingUserId === user.id ? '⏳' : '✅'} Approve
              </button>
              <button 
                className="button danger" 
                onClick={() => handleDeleteUser(user.id)}
                disabled={processingUserId === user.id}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#dc3545', cursor: processingUserId === user.id ? 'not-allowed' : 'pointer', opacity: processingUserId === user.id ? 0.5 : 1 }}
              >
                {processingUserId === user.id ? '⏳' : '🗑️'} Delete
              </button>
            </div>
          </div>
        ))}
            <PaginationControls userArray={filteredUsers} category="rejected_users" />
          </>
        )}
          </div>
        )}
      </div>
    );
  };


  return (
    <div>
      <div className="card">
        <h2>User Management</h2>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button className="button" onClick={onRefresh}>Refresh Users</button>
          <button
            className="button"
            style={{ backgroundColor: '#6f42c1' }}
            onClick={() => window.open(
              process.env.REACT_APP_ENVIRONMENT === 'prod'
                ? 'https://iefcustpolicystorageprd.blob.core.windows.net/skypathsos/register-prod.html'
                : 'https://iefcustpolicystorageprd.blob.core.windows.net/skypathsos/register.html',
              '_blank'
            )}
          >
            ➕ Provision B2C User
          </button>
        </div>
        
        {/* Summary Statistics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', margin: '20px 0' }}>
          <div style={{ padding: '15px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>{users.pending_users?.length || 0}</div>
            <div style={{ fontSize: '14px', color: '#856404' }}>⏳ Pending Approval</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', border: '2px solid #28a745', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{users.nbcu_users.length + users.telemundo_users.length + users.admins.length + users.superadmins.length}</div>
            <div style={{ fontSize: '14px', color: '#28a745' }}>✅ Approved Users</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8d7da', border: '2px solid #dc3545', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#721c24' }}>{users.rejected_users?.length || 0}</div>
            <div style={{ fontSize: '14px', color: '#721c24' }}>🚫 Rejected</div>
          </div>
        </div>
        
        {/* Pending users - show first for visibility */}
        {renderPendingUserList(users.pending_users, 'Pending Users Awaiting Approval')}
        
        {/* Rejected users */}
        {renderRejectedUserList(users.rejected_users, 'Rejected Users')}
        
        {/* Approved users */}
        {renderUserList(users.superadmins, 'Super Admins')}
        {renderUserList(users.admins, 'Admins')}
        {renderUserList(users.nbcu_users, 'NBCU Users')}
        {renderUserList(users.telemundo_users, 'Telemundo Users')}
      </div>

      {/* Domain Whitelist Management */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>🔐 Email Domain Whitelist</h2>
          <button className="button" onClick={fetchDomains} disabled={loadingDomains}>
            {loadingDomains ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
        
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Only users with email addresses from these domains can register. 
          Users from other domains will be blocked during registration.
        </p>
        
        {/* Add Domain Form */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <input
            type="text"
            className="input"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="Enter domain (e.g., nbcuni.com or @nbcuni.com)"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addDomain();
              }
            }}
            style={{ flex: 1 }}
          />
          <button className="button" onClick={addDomain}>
            ➕ Add Domain
          </button>
        </div>
        
        {/* Domains List */}
        {loadingDomains ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Loading domains...</p>
        ) : domains.length === 0 ? (
          <div style={{ 
            padding: '30px', 
            textAlign: 'center', 
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}>
            <p style={{ margin: 0, color: '#856404' }}>
              ⚠️ No domains whitelisted. Registration is currently blocked for all users.
            </p>
          </div>
        ) : (
          <div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1fr 100px', 
              gap: '10px', 
              padding: '10px', 
              backgroundColor: '#f5f5f5', 
              fontWeight: 'bold', 
              borderRadius: '4px', 
              marginBottom: '10px' 
            }}>
              <div>Domain</div>
              <div>Added By</div>
              <div>Added Date</div>
              <div>Actions</div>
            </div>
            
            {domains.map((domain) => (
              <div 
                key={domain.domain}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr 100px', 
                  gap: '10px', 
                  alignItems: 'center',
                  border: '1px solid #ddd', 
                  padding: '15px', 
                  margin: '5px 0',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  @{domain.domain}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {domain.addedBy || 'Unknown'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {domain.addedAt ? new Date(domain.addedAt).toLocaleDateString() : 'Unknown'}
                </div>
                <div>
                  <button 
                    className="button danger small"
                    onClick={() => removeDomain(domain.domain)}
                    style={{ width: '100%', fontSize: '12px' }}
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ))}
            
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#004085' }}>
                💡 <strong>Tip:</strong> Add domains like "nbcuni.com" (without @). 
                Users will only be able to register if their email matches one of these domains.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px', borderRadius: '8px',
            maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3>➕ Add New User</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Username *</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="John Doe"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Role *</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Station Type *</label>
              <select
                value={newUser.stationType}
                onChange={(e) => setNewUser({ ...newUser, stationType: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="NBCU">NBCU</option>
                <option value="Telemundo">Telemundo</option>
              </select>
            </div>

            {createUserResult && (
              <div style={{
                padding: '10px', marginBottom: '15px', borderRadius: '4px',
                backgroundColor: createUserResult.success ? '#d4edda' : '#f8d7da',
                border: `1px solid ${createUserResult.success ? '#c3e6cb' : '#f5c6cb'}`,
                color: createUserResult.success ? '#155724' : '#721c24'
              }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{createUserResult.message}</p>
                {createUserResult.success && createUserResult.temporaryPassword && (
                  <div>
                    <p style={{ margin: '0 0 5px 0' }}><strong>Temporary Password:</strong></p>
                    <code style={{
                      display: 'block', padding: '8px', backgroundColor: '#fff',
                      border: '1px solid #ccc', borderRadius: '4px',
                      fontFamily: 'monospace', fontSize: '14px'
                    }}>
                      {createUserResult.temporaryPassword}
                    </code>
                    <p style={{ margin: '10px 0 0 0', fontSize: '12px' }}>
                      ⚠️ Save this password! The user must change it on first login.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setCreateUserResult(null);
                }}
                style={{
                  padding: '10px 20px', borderRadius: '4px',
                  border: '1px solid #ddd', backgroundColor: '#f8f9fa',
                  cursor: 'pointer'
                }}
              >
                {createUserResult?.success ? 'Close' : 'Cancel'}
              </button>
              {!createUserResult?.success && (
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  style={{
                    padding: '10px 20px', borderRadius: '4px',
                    border: 'none', backgroundColor: '#28a745',
                    color: 'white', cursor: creatingUser ? 'not-allowed' : 'pointer'
                  }}
                >
                  {creatingUser ? 'Creating...' : 'Create User'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px', borderRadius: '8px',
            maxWidth: '700px', width: '90%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3>📤 Bulk Upload Users</h3>
            
            <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>CSV Format:</p>
              <code style={{ display: 'block', fontSize: '12px', fontFamily: 'monospace' }}>
                email,username,role,stationType
              </code>
              <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                Example:<br/>
                <code>john@example.com,John Doe,user,NBCU</code><br/>
                <code>jane@example.com,Jane Smith,admin,Telemundo</code>
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Paste CSV Data</label>
              <textarea
                value={bulkUsers}
                onChange={(e) => setBulkUsers(e.target.value)}
                placeholder="email,username,role,stationType&#10;john@example.com,John Doe,user,NBCU&#10;jane@example.com,Jane Smith,admin,Telemundo"
                rows={10}
                style={{
                  width: '100%', padding: '8px', borderRadius: '4px',
                  border: '1px solid #ddd', fontFamily: 'monospace', fontSize: '12px'
                }}
              />
            </div>

            {bulkCreateResult && (
              <div style={{
                padding: '10px', marginBottom: '15px', borderRadius: '4px',
                backgroundColor: '#d4edda', border: '1px solid #c3e6cb'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#155724' }}>Upload Results</h4>
                <p style={{ margin: '0 0 10px 0', color: '#155724' }}>
                  ✅ Successful: {bulkCreateResult.summary?.successful || 0} / {bulkCreateResult.summary?.total || 0}
                </p>
                {bulkCreateResult.summary?.failed > 0 && (
                  <p style={{ margin: '0 0 10px 0', color: '#721c24' }}>
                    ❌ Failed: {bulkCreateResult.summary.failed}
                  </p>
                )}
                
                {bulkCreateResult.results?.successful?.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong>Created Users & Passwords:</strong>
                    <div style={{ maxHeight: '200px', overflow: 'auto', marginTop: '5px' }}>
                      {bulkCreateResult.results.successful.map((u, i) => (
                        <div key={i} style={{
                          padding: '8px', marginBottom: '5px',
                          backgroundColor: '#fff', border: '1px solid #ddd',
                          borderRadius: '4px', fontSize: '12px'
                        }}>
                          <div><strong>{u.email}</strong> ({u.username})</div>
                          <div style={{ fontFamily: 'monospace', color: '#666' }}>
                            Password: {u.temporaryPassword}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {bulkCreateResult.results?.failed?.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: '#721c24' }}>Failed:</strong>
                    <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '5px' }}>
                      {bulkCreateResult.results.failed.map((u, i) => (
                        <div key={i} style={{
                          padding: '5px', marginBottom: '3px',
                          backgroundColor: '#f8d7da', border: '1px solid #f5c6cb',
                          borderRadius: '4px', fontSize: '11px', color: '#721c24'
                        }}>
                          {u.email}: {u.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkCreateResult(null);
                }}
                style={{
                  padding: '10px 20px', borderRadius: '4px',
                  border: '1px solid #ddd', backgroundColor: '#f8f9fa',
                  cursor: 'pointer'
                }}
              >
                {bulkCreateResult ? 'Close' : 'Cancel'}
              </button>
              {!bulkCreateResult && (
                <button
                  onClick={handleBulkUpload}
                  disabled={creatingUser}
                  style={{
                    padding: '10px 20px', borderRadius: '4px',
                    border: 'none', backgroundColor: '#17a2b8',
                    color: 'white', cursor: creatingUser ? 'not-allowed' : 'pointer'
                  }}
                >
                  {creatingUser ? 'Uploading...' : 'Upload Users'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Login and registration page
function LoginPage({ onLogin, onRegister, onCompleteNewPassword, isLoading, handleSSOLogin, ssoError, onClearSsoError }) {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({ email: '', session: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    stationType: 'NBCU'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null); // Clear previous messages
    
    if (isLoginMode) {
      setLoadingMessage('Signing in...');
      const result = await onLogin(formData.email, formData.password);
      if (result.requiresPasswordChange) {
        // Show password change modal
        setPasswordChangeData({ email: result.email, session: result.session });
        setShowPasswordChangeModal(true);
        setMessage({ type: 'info', text: result.message || 'You must change your temporary password.' });
      } else if (!result.success) {
        setMessage({ 
          type: 'error', 
          text: result.error || 'Login failed'
        });
      }
      // If successful, the user state will update and LoginPage will unmount
    } else {
      setLoadingMessage('Creating account...');
      const result = await onRegister(formData.username, formData.email, formData.password, formData.stationType);
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: result.message || 'Registration successful! Your account is pending approval by an administrator. You will be able to log in once approved.'
        });
        // Clear form on success
        setFormData({ username: '', email: '', password: '', stationType: 'NBCU' });
      } else {
        setMessage({ 
          type: 'error', 
          text: result.error || 'Registration failed'
        });
      }
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage(null);
    
    // Validate passwords
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    
    setLoadingMessage('Changing password...');
    const result = await onCompleteNewPassword(passwordChangeData.email, passwordChangeData.session, newPassword);
    if (result.success) {
      setShowPasswordChangeModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: 'success', text: result.message || 'Password changed successfully! Logging you in...' });
      // User will be automatically logged in after successful password change
    } else {
      setMessage({ type: 'error', text: result.error || 'Password change failed' });
    }
  };

  // Get version info
  const packageVersion = process.env.REACT_APP_VERSION || '1.0.0';
  const buildTime = process.env.REACT_APP_BUILD_TIME || 'Unknown';
  const isDesktop = window.electronAPI ? true : false;
  const isDesktopApp = isDesktop;

  return (
    <div className="container" style={{ position: 'relative' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{
            color: 'white',
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: '600',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            {loadingMessage}
          </div>
        </div>
      )}
      
      <div className="card" style={{ maxWidth: '400px', margin: '50px auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h1 style={{ color: '#1f2937', fontSize: '32px', marginBottom: '12px' }}>SOS Alert System</h1>
        <h2 style={{ color: '#6b7280', fontSize: '18px', fontWeight: '500', marginBottom: '24px' }}>{isLoginMode ? 'Sign In' : 'Create Account'}</h2>
        
        {/* SSO Error Message */}
{ssoError && (() => {
          const isPendingApproval = ssoError.startsWith('PENDING_APPROVAL|');
          const message = isPendingApproval ? ssoError.split('|')[1] : ssoError;
          const isError = !isPendingApproval;
          
          return (
            <div style={{
              padding: '16px', 
              marginBottom: '20px',
              borderRadius: '8px',
              backgroundColor: isError ? '#fef2f2' : '#eff6ff',
              border: isError ? '1px solid #fee2e2' : '1px solid #dbeafe',
              color: isError ? '#991b1b' : '#1e40af',
              fontSize: '14px',
              lineHeight: '1.6',
              position: 'relative'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{isError ? '⚠️ SSO Login Failed' : 'ℹ️ Account Pending Approval'}</span>
                <button
                  onClick={onClearSsoError}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: isError ? '#991b1b' : '#1e40af',
                    fontSize: '20px',
                    padding: '0',
                    lineHeight: '1'
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div>{message}</div>
            </div>
          );
        })()}
        
        {/* Version Information */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '11px', 
          color: '#666', 
          marginBottom: '15px',
          padding: '8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}>
          <div>📱 {isDesktopApp ? 'Desktop App' : 'Web App'} v{packageVersion}</div>
          <div>🕐 Built: {new Date(buildTime).toLocaleString()}</div>
          {isDesktopApp && <div>🔄 Auto-updates enabled</div>}
        </div>
        
        {/* Success/Error Messages */}
        {message && (
          <div style={{ 
            padding: '12px 16px', 
            marginBottom: '16px',
            borderRadius: '6px',
            backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${message.type === 'success' ? '#d1fae5' : '#fee2e2'}`,
            color: message.type === 'success' ? '#047857' : '#991b1b',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        {/* SSO Login Buttons (only show in login mode) */}
        {isLoginMode && (SSO_CONFIG.samlEnabled || SSO_CONFIG.oidcEnabled) && (
          <div style={{ marginBottom: '24px' }}>
            {SSO_CONFIG.samlEnabled && (
              <button
                type="button"
                onClick={() => handleSSOLogin(SSO_CONFIG.samlProviderName)}
                className="button"
                style={{
                  width: '100%',
                  backgroundColor: '#0078d4',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                disabled={isLoading}
              >
                <span>🔐</span>
                <span>Sign in with NBCU SSOID</span>
              </button>
            )}
            
            {SSO_CONFIG.oidcEnabled && (
              <button
                type="button"
                onClick={() => handleSSOLogin(SSO_CONFIG.oidcProviderName)}
                className="button"
                style={{
                  width: '100%',
                  backgroundColor: '#008272',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: process.env.REACT_APP_ENVIRONMENT === 'prod' ? 0.5 : 1
                }}
                disabled={isLoading || process.env.REACT_APP_ENVIRONMENT === 'prod'}
                title={process.env.REACT_APP_ENVIRONMENT === 'prod' ? 'Coming soon' : ''}
              >
                <span>🔑</span>
                <span>{process.env.REACT_APP_ENVIRONMENT === 'prod' ? 'Sign in with SSOB2C -Coming soon' : `Sign in with ${SSO_CONFIG.oidcProviderName}`}</span>
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '400px', margin: '0 20px' }}>
            <h2 style={{ marginBottom: '20px', color: '#1f2937' }}>Change Password</h2>
            
            {message && (
              <div style={{ 
                padding: '12px 16px', 
                marginBottom: '16px',
                borderRadius: '6px',
                backgroundColor: message.type === 'error' ? '#fef2f2' : '#eff6ff',
                border: `1px solid ${message.type === 'error' ? '#fee2e2' : '#dbeafe'}`,
                color: message.type === 'error' ? '#991b1b' : '#1e40af',
                fontSize: '14px'
              }}>
                {message.type === 'error' ? '❌' : 'ℹ️'} {message.text}
              </div>
            )}
            
            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#374151' }}>
                  New Password
                </label>
                <input
                  type="password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength="8"
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Must be at least 8 characters
                </small>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#374151' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength="8"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="submit" 
                  className="button"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button 
                  type="button" 
                  className="button"
                  onClick={() => {
                    setShowPasswordChangeModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setMessage(null);
                  }}
                  style={{ flex: 1, backgroundColor: '#6b7280' }}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Inbox Dashboard for viewing message history
function InboxDashboard({ messages, onRefresh, onDeleteMessage, userRole }) {
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatTimeLeft = (timestamp) => {
    if (!timestamp) return 'No expiry';
    
    const expiryTime = timestamp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeLeft = expiryTime - now;
    
    if (timeLeft <= 0) return 'Expired';
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    } else {
      return `${minutes}m left`;
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>📥 Message Inbox</h2>
        <button className="button" onClick={onRefresh}>
          🔄 Refresh
        </button>
      </div>
      
      <p>Messages are automatically deleted 24 hours after acknowledgment.</p>
      
      {messages.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
          No messages in your inbox
        </p>
      ) : (
        <div className="message-list">
          {messages.map(message => (
            <div key={`${message.id}-${message.timestamp}`} className="message-item">
              <div className="message-header">
                <strong>From: {message.createdByUsername}</strong>
                <span className="message-date">
                  {formatDate(message.acknowledgedAt || message.createdAt)}
                </span>
              </div>
              
              <div className="message-content">
                <p>{message.message}</p>
              </div>
              
              <div className="message-footer">
                <div className="message-info">
                  <span>Target: {message.targetType === 'specific' ? 'Individual' : message.targetType}</span>
                  {message.expiresAt && (
                    <span className="expiry-info">
                      ⏰ {formatTimeLeft(message.expiresAt)}
                    </span>
                  )}
                </div>
                
                {userRole === 'superadmin' && (
                  <button 
                    className="button danger small"
                    onClick={() => onDeleteMessage(message.id)}
                  >
                    🗑️ Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin Sent Messages Component - Professional expandable layout matching user inbox
function AdminSentMessages({ messages = [], loading = false, onRefresh, onDelete, token }) {
  const [expanded, setExpanded] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [userMap, setUserMap] = useState({});
  
  // Fetch users to map IDs to names/emails
  useEffect(() => {
    if (!token) {
      console.log('⚠️ AdminSentMessages: Skipping user fetch - no token');
      return;
    }
    
    const fetchUsersForModal = async () => {
      try {
        console.log('📋 AdminSentMessages: Fetching users for modal');
        const response = await fetch(`${API_BASE_URL}/users`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          const users = data.users;
          
          // Create map: userId -> {username, email}
          const map = {};
          [users.superadmins, users.admins, users.nbcu_users, users.telemundo_users, users.pending_users, users.rejected_users]
            .flat()
            .forEach(user => {
              map[user.id] = { username: user.username, email: user.email };
            });
          
          setUserMap(map);
          console.log('User map created with', Object.keys(map).length, 'users');
        }
      } catch (error) {
        console.error('Error fetching users for modal:', error);
      }
    };
    
    fetchUsersForModal();
  }, [token]);
  
  const getTargetDisplay = (msg) => {
    if (msg.targetType === 'specific') return '🎯 Specific User';
    if (msg.targetType === 'NBCU') return '🏢 NBCU Station';
    return '🌐 Telemundo Station';
  };
  
  const getTimeRemaining = (createdAt) => {
    const expiryTime = createdAt + (24 * 60 * 60 * 1000);
    const now = Date.now();
    const remaining = expiryTime - now;
    
    if (remaining <= 0) return 'Expired';
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };
  
  const getDeliveryStats = (msg) => {
    const stats = { pending: 0, delivered: 0, seen: 0 };
    if (!msg.deliveryStatus) return stats;
    
    Object.values(msg.deliveryStatus).forEach(status => {
      if (status === 'seen') stats.seen++;
      else if (status === 'delivered') stats.delivered++;
      else stats.pending++;
    });
    return stats;
  };
  
  const renderDeliveryStatus = (msg) => {
    const stats = getDeliveryStats(msg);
    return (
      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '8px 12px',
        background: '#f3f4f6',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '12px'
      }}
      onClick={() => setDetailModal(msg)}
      onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
      onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
      >
        {stats.seen > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3b82f6' }}>
            <span style={{ fontWeight: 'bold' }}>✓✓</span>
            <span>{stats.seen} Seen</span>
          </div>
        )}
        {stats.delivered > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280' }}>
            <span style={{ fontWeight: 'bold' }}>✓✓</span>
            <span>{stats.delivered} Delivered</span>
          </div>
        )}
        {stats.pending > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#d1d5db' }}>
            <span style={{ fontWeight: 'bold' }}>✓</span>
            <span>{stats.pending} Pending</span>
          </div>
        )}
      </div>
    );
  };
  
  const renderMessage = (msg) => (
    <div key={msg.id} style={{
      borderLeft: '4px solid #3b82f6',
      padding: '16px',
      marginBottom: '12px',
      borderRadius: '8px',
      background: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: '0', fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>
            {msg.message.substring(0, 60)}{msg.message.length > 60 ? '...' : ''}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
            {new Date(msg.createdAt).toLocaleString()}
          </p>
        </div>
        {onDelete && (
          <button onClick={() => onDelete(msg.id)} style={{
            background: 'none',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '4px 8px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.color = '#ef4444'}
          onMouseOut={(e) => e.target.style.color = '#999'}
          >
            🗑️
          </button>
        )}
      </div>
      
      {/* Target and Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>To:</span><br />
          <span style={{ color: '#1f2937' }}>{getTargetDisplay(msg)}</span>
        </div>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Expires:</span><br />
          <span style={{ color: msg.createdAt + (24 * 60 * 60 * 1000) - Date.now() < 1 * 60 * 60 * 1000 ? '#ef4444' : '#1f2937' }}>
            {getTimeRemaining(msg.createdAt)}
          </span>
        </div>
      </div>
      
      {/* Delivery Status - Clickable */}
      {renderDeliveryStatus(msg)}
    </div>
  );
  
  const displayedMessages = expanded ? messages : messages.slice(0, 2);
  
  return (
    <div>
      {/* Header */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
        cursor: messages.length > 2 ? 'pointer' : 'default',
        transition: 'all 0.2s',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
      onClick={() => messages.length > 2 && setExpanded(!expanded)}
      onMouseOver={(e) => messages.length > 2 && (e.currentTarget.style.opacity = '0.9')}
      onMouseOut={(e) => messages.length > 2 && (e.currentTarget.style.opacity = '1')}
      >
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>📤 Sent Messages</h2>
          <p style={{ margin: '0', opacity: 0.9, fontSize: '13px' }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''} • {expanded ? 'Showing all' : `Showing ${Math.min(2, messages.length)} of ${messages.length}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={(e) => { e.stopPropagation(); onRefresh(); }} style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            🔄 Refresh
          </button>
          {messages.length > 2 && (
            <button style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              {expanded ? '▼ Collapse' : '▶ View All'}
            </button>
          )}
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="card" style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
          Loading messages...
        </div>
      )}
      
      {/* Empty State */}
      {!loading && messages.length === 0 && (
        <div className="card" style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#999',
          borderRadius: '12px',
          background: '#f9fafb'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
          <p style={{ margin: '0', fontSize: '16px', fontWeight: '500' }}>No messages sent yet</p>
        </div>
      )}
      
      {/* Messages List */}
      {!loading && messages.length > 0 && (
        <div>
          {displayedMessages.map((msg) => renderMessage(msg))}
        </div>
      )}
      
      {/* Detail Modal */}
      {detailModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
        onClick={() => setDetailModal(null)}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: '0', fontSize: '18px', color: '#1f2937' }}>Delivery Status</h2>
              <button onClick={() => setDetailModal(null)} style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999'
              }}>×</button>
            </div>
            
            <div style={{ marginBottom: '20px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
              <p style={{ margin: '0', fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>
                {detailModal.message.substring(0, 100)}{detailModal.message.length > 100 ? '...' : ''}
              </p>
            </div>
            
            {detailModal.deliveryStatus && Object.keys(detailModal.deliveryStatus).length > 0 ? (
              <div>
                {['seen', 'delivered', 'pending'].map((status) => {
                  const users = Object.entries(detailModal.deliveryStatus)
                    .filter(([_, s]) => (status === 'pending' ? s !== 'seen' && s !== 'delivered' : s === status))
                    .map(([uid, _]) => uid);
                  
                  if (users.length === 0) return null;
                  
                  let icon = '✓';
                  let label = 'Pending';
                  let color = '#d1d5db';
                  
                  if (status === 'seen') {
                    icon = '✓✓';
                    label = 'Seen';
                    color = '#3b82f6';
                  } else if (status === 'delivered') {
                    icon = '✓✓';
                    label = 'Delivered';
                    color = '#6b7280';
                  }
                  
                  return (
                    <div key={status} style={{ marginBottom: '12px' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        <span style={{ color: color }}>{icon}</span> {label}
                      </p>
                      {users.map(uid => {
                        const userData = userMap[uid];
                        return (
                          <div key={uid} style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '4px', marginBottom: '4px', fontSize: '13px', color: '#1f2937' }}>
                            <div style={{ fontWeight: '500' }}>{userData?.username || 'Unknown User'}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{userData?.email || uid}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>No delivery status available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
